import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GitMerge, X } from "lucide-react";

const ETIQUETAS = {
  vehiculos: "vehículos", repuestos: "repuestos", catalogo_referencia: "líneas de catálogo de referencia",
  frt_operaciones: "líneas de mano de obra (FRT)", plantillas: "plantillas",
  cronogramas: "cronogramas", cotizaciones: "cotizaciones",
};

const tieneCatalogo = (m) => (m.conteos?.catalogo_referencia || 0) > 0 || (m.conteos?.plantillas || 0) > 0;
const mejorDestino = (lista) => [...lista].sort((a, b) =>
  (tieneCatalogo(b) - tieneCatalogo(a)) || ((b.conteos?.vehiculos || 0) - (a.conteos?.vehiculos || 0)))[0];

export function ModeloFusionDialog({ abierto, ids, onCerrar, onFusionado }) {
  const [cargando, setCargando] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [destinoId, setDestinoId] = useState(null);
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [resumen, setResumen] = useState(null);
  const [ejecutando, setEjecutando] = useState(false);

  useEffect(() => {
    if (!abierto || !ids?.length) return;
    setCargando(true);
    setResumen(null);
    api.get("/modelos/referencias", { params: { ids: ids.join(",") } })
      .then((r) => {
        const lista = r.data.modelos || [];
        setModelos(lista);
        const mejor = mejorDestino(lista);
        if (mejor) { setDestinoId(mejor.id); setMarca(mejor.marca || ""); setModelo(mejor.modelo || ""); }
      })
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [abierto, ids]);

  const elegirDestino = (m) => { setDestinoId(m.id); setMarca(m.marca || ""); setModelo(m.modelo || ""); };

  const quitarModelo = (id) => {
    setModelos((prev) => {
      const next = prev.filter((m) => m.id !== id);
      if (destinoId === id) {
        const mejor = mejorDestino(next);
        if (mejor) { setDestinoId(mejor.id); setMarca(mejor.marca || ""); setModelo(mejor.modelo || ""); }
        else setDestinoId(null);
      }
      return next;
    });
  };

  const origenIds = modelos.filter((m) => m.id !== destinoId).map((m) => m.id);

  const cargarResumen = useCallback(() => {
    if (!destinoId || !origenIds.length) { setResumen(null); return; }
    api.get("/modelos/referencias", { params: { ids: origenIds.join(",") } })
      .then((r) => setResumen(r.data.combinado || {}))
      .catch(() => setResumen(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinoId, modelos]);

  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  const partesResumen = () => {
    if (!resumen) return "";
    return Object.entries(resumen)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${v} ${ETIQUETAS[k] || k}`)
      .join(", ") || "sin referencias asociadas";
  };

  const fusionar = async () => {
    if (!destinoId || origenIds.length < 1) { toast.error("Elija el modelo que se conserva"); return; }
    const nombreFinal = `${(marca || "").trim()} ${(modelo || "").trim()}`.trim();
    const texto = `Se unirán ${modelos.length} modelos en "${nombreFinal}". Se reasignarán ${partesResumen()}. ` +
      `Los otros ${origenIds.length} modelo(s) se eliminarán. Esta acción no se puede deshacer.`;
    if (!window.confirm(texto)) return;
    setEjecutando(true);
    try {
      const { data } = await api.post("/modelos/fusionar", {
        destino_id: destinoId, origen_ids: origenIds, marca, modelo,
      });
      toast.success(`Modelos fusionados en "${data.destino.nombre_mostrar}" (${data.eliminados} eliminados)`);
      onFusionado?.();
      onCerrar?.();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setEjecutando(false); }
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) onCerrar?.(); }}>
      <DialogContent data-testid="modelo-fusion-dialog" className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge size={18} /> Fusionar modelos</DialogTitle>
          <DialogDescription>
            Elija el modelo que se conserva; los demás se reasignarán a él y se eliminarán.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3">
          {cargando && <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>}
          {!cargando && modelos.map((m) => {
            const esDestino = m.id === destinoId;
            return (
              <label key={m.id} data-testid={`fusion-modelo-${m.id}`}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${esDestino ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="destino" data-testid={`fusion-destino-radio-${m.id}`}
                  checked={esDestino} onChange={() => elegirDestino(m)} className="mt-1 accent-indigo-600" />
                <div className="flex-1 min-w-0 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">{m.nombre_mostrar}</span>
                    {esDestino && <span className="text-[10px] font-bold uppercase text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded">Se conserva</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span>Cilindrada: {m.grupo_cilindrada || "—"}</span>
                    <span>Tarifa: {m.grupo_tarifa || "—"}</span>
                    <span>Aceite: {m.intervalo_aceite_km ? `${m.intervalo_aceite_km} km` : "—"}</span>
                    <span>Años: {m.anio_inicio ? `${m.anio_inicio}–${m.anio_fin || "actual"}` : "—"}</span>
                    <span className="font-medium text-slate-600" data-testid={`fusion-total-${m.id}`}>{m.total} registros dependen</span>
                  </div>
                </div>
                <button type="button" data-testid={`fusion-quitar-${m.id}`}
                  onClick={(e) => { e.preventDefault(); quitarModelo(m.id); }}
                  className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                  <X size={13} />
                </button>
              </label>
            );
          })}

          {!cargando && destinoId && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-500">Marca final</label>
                <Input data-testid="fusion-marca-input" value={marca} onChange={(e) => setMarca(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-500">Modelo final</label>
                <Input data-testid="fusion-modelo-input" value={modelo} onChange={(e) => setModelo(e.target.value)} />
              </div>
            </div>
          )}

          {!cargando && destinoId && (
            <div data-testid="fusion-resumen" className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              Se reasignarán <b>{partesResumen()}</b> al modelo <b>{`${marca} ${modelo}`.trim()}</b> y se eliminarán {origenIds.length} modelo(s). Esta acción no se puede deshacer.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 shrink-0">
          <Button variant="outline" data-testid="fusion-cancelar-btn"
            className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => onCerrar?.()}>Cancelar</Button>
          <Button data-testid="fusion-confirmar-btn" onClick={fusionar}
            disabled={ejecutando || cargando || !destinoId || origenIds.length < 1}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
            {ejecutando ? "Fusionando…" : "Fusionar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
